import { useState, useEffect } from "react"
import {
  Filter,
  Download,
  Edit,
  Save,
  X,
  CheckCircle,
  XCircle,
  Clock,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  ChevronDown,
  FileText,
  FileSpreadsheet,
  File,
} from "lucide-react"
import { HRService } from "@/features/hr"
import { PayReportShimmerTable } from "@/shared/components"
import { useAuth } from "@/features/auth"

interface PayReportManagerProps {
  onError: (error: string) => void
}

interface FilterState {
  agentId?: string
  teamId?: string
  status?: string
  payPeriodStart?: string
  payPeriodEnd?: string
  search?: string
}

interface Team {
  id: string
  name: string
  organizationId: string
}

interface Agent {
  id: string
  agentCode: string
  user: {
    firstName: string
    lastName: string
    email: string
  }
  team?: {
    id: string
    name: string
  }
}

interface EnhancedPayReport {
  id: string
  agentId: string
  teamName?: string
  organizationType: "EM" | "DM" | "CM" | "BM" | "AM"
  commissionRate: number
  totalSalesCount: number
  salesAmount: number
  overrideRate: number
  totalOverrideCount: number
  overrideAmount: number
  bonuses: number
  adjustments: number
  totalCommission: number
  totalDownlineRevenue: number
  activeDownlineCount: number
  inactiveDownlineCount: number
  status: "PENDING" | "APPROVED"
  payPeriodStart: string
  payPeriodEnd: string
  createdAt: string
  updatedAt: string
  agentName: string
  organizationName?: string
  // Hierarchy columns
  emLevel?: string
  dmLevel?: string
  cmLevel?: string
  bmLevel?: string
  amLevel?: string
}

export default function PayReportManager({ onError }: PayReportManagerProps) {
  const { user } = useAuth()
  const [allReports, setAllReports] = useState<EnhancedPayReport[]>([])
  const [paginatedReports, setPaginatedReports] = useState<EnhancedPayReport[]>([])
  const [loading, setLoading] = useState(false)
  const [filters, setFilters] = useState<FilterState>({})
  const [editingReport, setEditingReport] = useState<string | null>(null)
  const [editData, setEditData] = useState<{
    status: string
    bonuses?: number | string
    adjustments?: number | string
  }>({
    status: "",
    bonuses: "",
    adjustments: "",
  })
  const [showFilters, setShowFilters] = useState(false)
  
  // New state for dropdowns
  const [teams, setTeams] = useState<Team[]>([])
  const [agents, setAgents] = useState<Agent[]>([])
  const [loadingTeams, setLoadingTeams] = useState(false)
  const [loadingAgents, setLoadingAgents] = useState(false)
  const [serverPagination, setServerPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0
  })
  const [filtersApplied, setFiltersApplied] = useState(false)

  // Check if user is an agent (hide revenue column for agents)
  const isAgent = user?.role === "agent"
  const [showExportDropdown, setShowExportDropdown] = useState(false)

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage, setItemsPerPage] = useState(10)
  const [sortField, setSortField] = useState<string>("payPeriodStart")
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("desc")


  useEffect(() => {
    loadTeams()
    loadAgents()
    loadReports() // Load all records initially
  }, [])

  // Reset to page 1 when filters change (but not when data is loaded)
  useEffect(() => {
    setCurrentPage(1)
  }, [filters])

  // Reload data when sorting, pagination, or items per page changes
  useEffect(() => {
    if (teams.length > 0 && agents.length > 0) {
      setLoading(true)
      loadReports()
    }
  }, [currentPage, itemsPerPage])

  useEffect(() => {
    applyFiltersAndPagination()
  }, [allReports, sortField, sortDirection])

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (showExportDropdown) {
        const target = event.target as Element
        if (!target.closest('.export-dropdown')) {
          setShowExportDropdown(false)
        }
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [showExportDropdown])

  const loadTeams = async () => {
    setLoadingTeams(true)
    try {
      const response = await HRService.getTeams()
      setTeams(response)
    } catch (error) {
      console.error('Failed to load teams:', error)
      onError('Failed to load teams')
    } finally {
      setLoadingTeams(false)
    }
  }

  const loadAgents = async () => {
    setLoadingAgents(true)
    try {
      const response = await HRService.getAgents()
      setAgents(response)
    } catch (error) {
      console.error('Failed to load agents:', error)
      onError('Failed to load agents')
    } finally {
      setLoadingAgents(false)
    }
  }

  const applyFilters = () => {
    setFiltersApplied(true)
    setCurrentPage(1) // Reset to first page when applying filters
    loadReports() // Load reports with applied filters
  }

  const clearFilters = () => {
    setFilters({})
    setFiltersApplied(false)
    setCurrentPage(1)
    loadReports() // Reload all records when clearing filters
  }

  const loadReports = async () => {
    setLoading(true)
    try {
      const apiFilters: any = {
        page: currentPage,
        limit: itemsPerPage,
      }

      // Add filters only if they have values
      if (filters.agentId) apiFilters.agentId = filters.agentId
      if (filters.teamId) apiFilters.teamId = filters.teamId
      if (filters.status) apiFilters.status = filters.status
      if (filters.payPeriodStart) apiFilters.payPeriodStart = filters.payPeriodStart
      if (filters.payPeriodEnd) apiFilters.payPeriodEnd = filters.payPeriodEnd

      const response = await HRService.getPayReports(apiFilters)
     
      // Check if response and data exist
      if (!response || !response.payReports) {
        console.error('Invalid response from API:', response)
        setAllReports([])
        return
      }
      
      // Handle new response format
      const reports = response.payReports || []
      const pagination = {
        page: response.pagination?.page || 1,
        limit: response.pagination?.limit || itemsPerPage,
        total: response.pagination?.total || reports.length,
        pages: response.pagination?.totalPages || 1,
      }

    
      // Don't override currentPage from API response - we manage it on frontend
      // The API response pagination.page might be different due to server-side logic
      
      // Transform the data to match our EnhancedPayReport interface
      const enhancedReports: EnhancedPayReport[] = reports.map((report: any) => {
        try {
          return {
            id: report.id || '',
            agentId: report.agentId || '',
            teamName: report.team?.name || undefined,
            organizationType: report.organizationType || 'AM',
            commissionRate: Number(report.commissionRate) || 0,
            totalSalesCount: Number(report.totalSalesCount) || 0,
            salesAmount: Number(report.salesAmount) || 0,
            overrideRate: Number(report.overrideRate) || 0,
            totalOverrideCount: Number(report.totalOverrideCount) || 0,
            overrideAmount: Number(report.overrideAmount) || 0,
            bonuses: Number(report.bonuses) || 0,
            adjustments: Number(report.adjustments) || 0,
            totalCommission: Number(report.totalCommission) || 0,
            totalDownlineRevenue: Number(report.totalDownlineRevenue) || 0,
            activeDownlineCount: Number(report.activeDownlineCount) || 0,
            inactiveDownlineCount: Number(report.inactiveDownlineCount) || 0,
            status: report.status || 'PENDING',
            payPeriodStart: report.payPeriodStart || '',
            payPeriodEnd: report.payPeriodEnd || '',
            createdAt: report.createdAt || '',
            updatedAt: report.updatedAt || '',
            agentName: report.agent?.user 
              ? `${report.agent.user.firstName || ''} ${report.agent.user.lastName || ''}`.trim() || "Unknown Agent"
              : report.agent?.agentCode || "Unknown Agent",
            organizationName: report.organization?.name || "Unknown Organization",
            // Hierarchy levels - now populated from the backend
            emLevel: report.emLevel || null,
            dmLevel: report.dmLevel || null,
            cmLevel: report.cmLevel || null,
            bmLevel: report.bmLevel || null,
            amLevel: report.amLevel || null,
          }
        } catch (error) {
          console.error('Error transforming report:', report, error)
          return null
        }
      }).filter(Boolean) as EnhancedPayReport[]

      setAllReports(enhancedReports)
      
      // Update server pagination info
      setServerPagination({
        page: pagination.page,
        limit: pagination.limit,
        total: pagination.total,
        totalPages: pagination.pages
      })
    } catch (error) {
      onError(error instanceof Error ? error.message : "Failed to load pay reports")
    } finally {
      setLoading(false)
    }
  }

  const applyFiltersAndPagination = () => {
    // Since we're using server-side filtering and pagination,
    // we just need to apply client-side sorting to the current page data
    let filtered = [...allReports]

    // Apply sorting
    filtered.sort((a, b) => {
      let aValue: any = a[sortField as keyof EnhancedPayReport]
      let bValue: any = b[sortField as keyof EnhancedPayReport]

      if (sortField === "commissionAmount") {
        aValue = Number(aValue)
        bValue = Number(bValue)
      }

      if (aValue < bValue) return sortDirection === "asc" ? -1 : 1
      if (aValue > bValue) return sortDirection === "asc" ? 1 : -1
      return 0
    })

    setPaginatedReports(filtered)
  }

  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc")
    } else {
      setSortField(field)
      setSortDirection("asc")
    }
  }

  const handlePageChange = (page: number) => {
    setCurrentPage(page)
  }

  const handleItemsPerPageChange = (items: number) => {
    setItemsPerPage(items)
    setCurrentPage(1)
  }

  const handleEdit = (report: EnhancedPayReport) => {
    setEditingReport(report.id)
    setEditData({
      bonuses: report.bonuses.toString(),
      adjustments: report.adjustments.toString(),
      status: report.status,
    })
  }

  const handleSave = async (reportId: string) => {
    try {
      const updateData = {
        status: editData.status,
        bonuses: editData.bonuses === "" ? 0 : Number.parseFloat(editData.bonuses as string) || 0,
        adjustments: editData.adjustments === "" ? 0 : Number.parseFloat(editData.adjustments as string) || 0,
      }
      
      await HRService.updatePayReport(reportId, updateData)
      setEditingReport(null)
      setEditData({ status: "", bonuses: "", adjustments: "" })
      loadReports()
    } catch (error) {
      onError(error instanceof Error ? error.message : "Failed to update pay report")
    }
  }

  const handleCancel = () => {
    setEditingReport(null)
    setEditData({ status: "", bonuses: "", adjustments: "" })
  }


  const exportReports = (format: "csv" | "excel" | "pdf") => {
    // Use current page data for export
    const reportsToExport = paginatedReports

    if (format === "csv") {
      const csvContent =
        "data:text/csv;charset=utf-8," +
        "Organization,Team,Agent,Agent ID,Commission Rate,Total Sales Count,Sales Amount,Override Rate,Total Override Count,Override Amount,Bonuses,Adjustments,Total Commission,Active Downline Count,Inactive Downline Count,Total Downline Count,Pay Period,Status\n" +
        reportsToExport
          .map(
            (r) =>
              `"${r.organizationType}","${r.teamName || 'N/A'}","${r.agentName}","${r.agentId}",${r.commissionRate},${r.totalSalesCount},${r.salesAmount},${r.overrideRate},${r.totalOverrideCount},${r.overrideAmount},${r.bonuses},${r.adjustments},${r.totalCommission},${r.activeDownlineCount},${r.inactiveDownlineCount},${r.activeDownlineCount + r.inactiveDownlineCount},"${formatPayPeriod(r.payPeriodStart, r.payPeriodEnd)}","${r.status}"`,
          )
          .join("\n")

      const encodedUri = encodeURI(csvContent)
      const link = document.createElement("a")
      link.setAttribute("href", encodedUri)
      link.setAttribute("download", `pay_reports_${new Date().toISOString().split("T")[0]}.csv`)
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    } else if (format === "excel") {
      // For Excel, we'll create a CSV with .xlsx extension
      // In a real implementation, you'd use a library like xlsx
      const csvContent =
        "data:text/csv;charset=utf-8," +
        "Organization,Team,Agent,Agent ID,Commission Rate,Total Sales Count,Sales Amount,Override Rate,Total Override Count,Override Amount,Bonuses,Adjustments,Total Commission,Active Downline Count,Inactive Downline Count,Total Downline Count,Pay Period,Status\n" +
        reportsToExport
          .map(
            (r) =>
              `"${r.organizationType}","${r.teamName || 'N/A'}","${r.agentName}","${r.agentId}",${r.commissionRate},${r.totalSalesCount},${r.salesAmount},${r.overrideRate},${r.totalOverrideCount},${r.overrideAmount},${r.bonuses},${r.adjustments},${r.totalCommission},${r.activeDownlineCount},${r.inactiveDownlineCount},${r.activeDownlineCount + r.inactiveDownlineCount},"${formatPayPeriod(r.payPeriodStart, r.payPeriodEnd)}","${r.status}"`,
          )
          .join("\n")

      const encodedUri = encodeURI(csvContent)
      const link = document.createElement("a")
      link.setAttribute("href", encodedUri)
      link.setAttribute("download", `pay_reports_${new Date().toISOString().split("T")[0]}.xlsx`)
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    } else if (format === "pdf") {
      // For PDF, we'll create a simple HTML table and use browser print
      const htmlContent = `
        <!DOCTYPE html>
        <html>
        <head>
          <title>Pay Reports - ${new Date().toISOString().split("T")[0]}</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 20px; }
            table { border-collapse: collapse; width: 100%; }
            th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
            th { background-color: #f2f2f2; }
            .header { text-align: center; margin-bottom: 20px; }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>Pay Reports</h1>
            <p>Generated on: ${new Date().toLocaleDateString()}</p>
          </div>
          <table>
            <thead>
              <tr>
                <th>Organization</th>
                <th>Team</th>
                <th>Agent</th>
                <th>Agent ID</th>
                <th>Commission Rate</th>
                <th>Total Sales Count</th>
                <th>Sales Amount</th>
                <th>Override Rate</th>
                <th>Total Override Count</th>
                <th>Override Amount</th>
                <th>Bonuses</th>
                <th>Adjustments</th>
                <th>Total Commission</th>
                <th>Active Downline Count</th>
                <th>Inactive Downline Count</th>
                <th>Total Downline Count</th>
                <th>Pay Period</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              ${reportsToExport
                .map(
                  (r) => `
                <tr>
                  <td>${r.organizationType}</td>
                  <td>${r.teamName || 'N/A'}</td>
                  <td>${r.agentName}</td>
                  <td>${r.agentId}</td>
                  <td>${r.commissionRate}</td>
                  <td>${r.totalSalesCount}</td>
                  <td>${r.salesAmount}</td>
                  <td>${r.overrideRate}</td>
                  <td>${r.totalOverrideCount}</td>
                  <td>${r.overrideAmount}</td>
                  <td>${r.bonuses}</td>
                  <td>${r.adjustments}</td>
                  <td>${r.totalCommission}</td>
                  <td>${r.activeDownlineCount}</td>
                  <td>${r.inactiveDownlineCount}</td>
                  <td>${r.activeDownlineCount + r.inactiveDownlineCount}</td>
                  <td>${formatPayPeriod(r.payPeriodStart, r.payPeriodEnd)}</td>
                  <td>${r.status}</td>
                </tr>
              `,
                )
                .join('')}
            </tbody>
          </table>
        </body>
        </html>
      `

      const printWindow = window.open('', '_blank')
      if (printWindow) {
        printWindow.document.write(htmlContent)
        printWindow.document.close()
        printWindow.print()
      }
    }
  }


  const getStatusBadge = (status: string) => {
    const baseClasses = "inline-flex px-2 py-1 text-xs font-semibold rounded-full"
    switch (status.toLowerCase()) {
      case "approved":
        return `${baseClasses} bg-green-100 text-green-800`
      case "pending":
        return `${baseClasses} bg-yellow-100 text-yellow-800`
      default:
        return `${baseClasses} bg-gray-100 text-gray-800`
    }
  }

  const getStatusIcon = (status: string) => {
    switch (status.toLowerCase()) {
      case "approved":
        return <CheckCircle className="w-4 h-4 text-green-600" />
      case "pending":
        return <Clock className="w-4 h-4 text-yellow-600" />
      default:
        return <XCircle className="w-4 h-4 text-gray-600" />
    }
  }

  const formatPayPeriod = (start: string, end: string) => {
    const startDate = new Date(start)
    const endDate = new Date(end)
    return `${startDate.toLocaleDateString()} - ${endDate.toLocaleDateString()}`
  }

  const getSortIcon = (field: string) => {
    if (sortField !== field) return null
    return sortDirection === "asc" ? "↑" : "↓"
  }

  const safeToFixed = (value: any, decimals: number = 2): string => {
    const num = Number(value)
    return isNaN(num) ? '0.00' : num.toFixed(decimals)
  }

  const safeToLocaleString = (value: any): string => {
    const num = Number(value)
    return isNaN(num) ? '0' : num.toLocaleString()
  }

  // Pagination calculations - using server-side pagination
  const totalPages = serverPagination.totalPages
  const totalItems = serverPagination.total

  // Summary calculations - using current page data
  const paidReports = paginatedReports.filter((r) => r.status === "APPROVED")
  const unpaidReports = paginatedReports.filter((r) => r.status === "PENDING")
  const totalPaid = paidReports.reduce((sum, r) => sum + r.totalCommission, 0)
  const totalUnpaid = unpaidReports.reduce((sum, r) => sum + r.totalCommission, 0)

  const renderPagination = () => {
    const pages = []
    const maxVisiblePages = 5
    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2))
    const endPage = Math.min(totalPages, startPage + maxVisiblePages - 1)

    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1)
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i)
    }

    return (
      <div className="flex items-center justify-between px-4 py-3 bg-white border-t border-accent-200 sm:px-6">
        <div className="flex justify-between flex-1 sm:hidden">
          <button
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
            className="relative inline-flex items-center px-4 py-2 text-sm font-medium text-accent-700 bg-white border border-accent-300 rounded-md hover:bg-accent-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Previous
          </button>
          <button
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            className="relative ml-3 inline-flex items-center px-4 py-2 text-sm font-medium text-accent-700 bg-white border border-accent-300 rounded-md hover:bg-accent-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Next
          </button>
        </div>
        <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
          <div className="flex items-center space-x-2">
            <p className="text-sm text-accent-700">
              Showing <span className="font-medium">{(currentPage - 1) * itemsPerPage + 1}</span> to{" "}
              <span className="font-medium">{Math.min(currentPage * itemsPerPage, totalItems)}</span> of{" "}
              <span className="font-medium">{totalItems}</span> results
            </p>
            <select
              value={itemsPerPage}
              onChange={(e) => handleItemsPerPageChange(Number(e.target.value))}
              className="ml-4 text-sm border border-accent-300 rounded-md px-2 py-1"
            >
              <option value={10}>10 per page</option>
              <option value={25}>25 per page</option>
              <option value={50}>50 per page</option>
              <option value={100}>100 per page</option>
            </select>
          </div>
          <div>
            <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
              <button
                onClick={() => handlePageChange(1)}
                disabled={currentPage === 1}
                className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-accent-300 bg-white text-sm font-medium text-accent-500 hover:bg-accent-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronsLeft className="h-5 w-5" />
              </button>
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className="relative inline-flex items-center px-2 py-2 border border-accent-300 bg-white text-sm font-medium text-accent-500 hover:bg-accent-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronLeft className="h-5 w-5" />
              </button>
              {pages.map((page) => {
                const isCurrentPage = page === currentPage
                return (
                  <button
                    key={page}
                    onClick={() => handlePageChange(page)}
                    className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                      isCurrentPage
                        ? "z-10 bg-primary-50 border-primary-500 text-primary-600"
                        : "bg-white border-accent-300 text-accent-500 hover:bg-accent-50"
                    }`}
                  >
                    {page}
                  </button>
                )
              })}
              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="relative inline-flex items-center px-2 py-2 border border-accent-300 bg-white text-sm font-medium text-accent-500 hover:bg-accent-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronRight className="h-5 w-5" />
              </button>
              <button
                onClick={() => handlePageChange(totalPages)}
                disabled={currentPage === totalPages}
                className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-accent-300 bg-white text-sm font-medium text-accent-500 hover:bg-accent-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronsRight className="h-5 w-5" />
              </button>
            </nav>
          </div>
        </div>
      </div>
    )
  }


  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
        <h2 className="text-2xl font-bold text-accent-900">Pay Reports Management</h2>
        <div className="flex flex-col sm:flex-row gap-2">
          {/* Export Dropdown */}
          <div className="relative export-dropdown">
            <button
              onClick={() => setShowExportDropdown(!showExportDropdown)}
              className="btn-outline flex items-center justify-center space-x-2"
            >
              <Download className="w-4 h-4" />
              <span>Export</span>
              <ChevronDown className="w-4 h-4" />
            </button>
            
            {showExportDropdown && (
              <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg z-10 border border-accent-200">
                <div className="py-1">
            <button
                    onClick={() => {
                      exportReports("csv")
                      setShowExportDropdown(false)
                    }}
                    className="flex items-center w-full px-4 py-2 text-sm text-accent-700 hover:bg-accent-50 transition-colors"
                  >
                    <FileText className="w-4 h-4 mr-3" />
                    Export as CSV
            </button>
            <button
                    onClick={() => {
                      exportReports("excel")
                      setShowExportDropdown(false)
                    }}
                    className="flex items-center w-full px-4 py-2 text-sm text-accent-700 hover:bg-accent-50 transition-colors"
                  >
                    <FileSpreadsheet className="w-4 h-4 mr-3" />
                    Export as Excel
                  </button>
                  <button
                    onClick={() => {
                      exportReports("pdf")
                      setShowExportDropdown(false)
                    }}
                    className="flex items-center w-full px-4 py-2 text-sm text-accent-700 hover:bg-accent-50 transition-colors"
                  >
                    <File className="w-4 h-4 mr-3" />
                    Export as PDF
            </button>
                </div>
              </div>
            )}
          </div>
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="btn-outline flex items-center justify-center space-x-2"
          >
            <Filter className="w-4 h-4" />
            <span>Filters</span>
          </button>
        </div>
      </div>


      {/* Filters Panel */}
      {showFilters && (
        <div className="card">
          <h3 className="text-lg font-semibold text-accent-900 mb-4">Filter Pay Reports</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-accent-700 mb-1">Search</label>
              <input
                type="text"
                className="input-field"
                placeholder="Search agent, team, organization..."
                value={filters.search || ""}
                onChange={(e) => setFilters({ ...filters, search: e.target.value })}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-accent-700 mb-1">Agent</label>
              <select
                className="input-field"
                value={filters.agentId || ""}
                onChange={(e) => setFilters({ ...filters, agentId: e.target.value })}
                disabled={loadingAgents}
              >
                <option value="">All Agents</option>
                {agents.map((agent) => (
                  <option key={agent.id} value={agent.id}>
                    {agent.user.firstName} {agent.user.lastName} ({agent.agentCode})
                  </option>
                ))}
              </select>
              {loadingAgents && (
                <p className="text-xs text-accent-500 mt-1">Loading agents...</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-accent-700 mb-1">Team</label>
              <select
                className="input-field"
                value={filters.teamId || ""}
                onChange={(e) => setFilters({ ...filters, teamId: e.target.value })}
                disabled={loadingTeams}
              >
                <option value="">All Teams</option>
                {teams.map((team) => (
                  <option key={team.id} value={team.id}>
                    {team.name}
                  </option>
                ))}
              </select>
              {loadingTeams && (
                <p className="text-xs text-accent-500 mt-1">Loading teams...</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-accent-700 mb-1">Status</label>
              <select
                className="input-field"
                value={filters.status || ""}
                onChange={(e) => setFilters({ ...filters, status: e.target.value })}
              >
                <option value="">All Status</option>
                <option value="APPROVED">Approved</option>
                <option value="PENDING">Pending</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-accent-700 mb-1">Pay Period Start (Sat)</label>
              <input
                type="date"
                className="input-field"
                value={filters.payPeriodStart || ""}
                onChange={(e) => setFilters({ ...filters, payPeriodStart: e.target.value })}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-accent-700 mb-1">Pay Period End (Fri)</label>
              <input
                type="date"
                className="input-field"
                value={filters.payPeriodEnd || ""}
                onChange={(e) => setFilters({ ...filters, payPeriodEnd: e.target.value })}
              />
            </div>
          </div>
          <div className="mt-4 flex gap-2">
            <button onClick={applyFilters} className="btn-primary">
              Apply Filters
            </button>
            <button onClick={clearFilters} className="btn-outline">
              Clear Filters
            </button>
          </div>
        </div>
      )}

      {/* Pay Reports Table */}
      <div className="card">
        {loading ? (
          <PayReportShimmerTable rows={itemsPerPage} />
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-accent-200">
                <thead className="bg-accent-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-accent-500 uppercase tracking-wider">
                      <div className="flex items-center space-x-1">
                        <span>EM Level</span>
                      </div>
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-accent-500 uppercase tracking-wider">
                      <div className="flex items-center space-x-1">
                        <span>DM Level</span>
                      </div>
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-accent-500 uppercase tracking-wider">
                      <div className="flex items-center space-x-1">
                        <span>CM Level</span>
                      </div>
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-accent-500 uppercase tracking-wider">
                      <div className="flex items-center space-x-1">
                        <span>BM Level</span>
                      </div>
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-accent-500 uppercase tracking-wider">
                      <div className="flex items-center space-x-1">
                        <span>AM Level</span>
                      </div>
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-accent-500 uppercase tracking-wider">
                      <div className="flex items-center space-x-1">
                        <span>Agent</span>
                      </div>
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-accent-500 uppercase tracking-wider">
                      Commission Rate
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-accent-500 uppercase tracking-wider">
                      Total Sales Count
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-accent-500 uppercase tracking-wider">
                      Sales
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-accent-500 uppercase tracking-wider">
                      Override Rate
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-accent-500 uppercase tracking-wider">
                      Total Override Count
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-accent-500 uppercase tracking-wider">
                      Override
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-accent-500 uppercase tracking-wider">
                      Bonuses
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-accent-500 uppercase tracking-wider">
                      Adjustments
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-accent-500 uppercase tracking-wider">
                      Commission
                    </th>
                    {!isAgent && (
                      <th className="px-6 py-3 text-left text-xs font-medium text-accent-500 uppercase tracking-wider">
                        Total Downline Revenue
                      </th>
                    )}
                    <th className="px-6 py-3 text-left text-xs font-medium text-accent-500 uppercase tracking-wider">
                      Active Downline Count
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-accent-500 uppercase tracking-wider">
                      Inactive Downline Count
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-accent-500 uppercase tracking-wider">
                      Total Downline Count
                    </th>
                    <th
                      className="px-6 py-3 text-left text-xs font-medium text-accent-500 uppercase tracking-wider cursor-pointer hover:bg-accent-100"
                      onClick={() => handleSort("status")}
                    >
                      Status {getSortIcon("status")}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-accent-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-accent-200">
                  {paginatedReports.map((report) => (
                    <tr key={report.id} className="hover:bg-accent-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="w-8 h-8 rounded-full flex items-center justify-center mr-3 text-white font-bold bg-purple-600">
                            E
                          </div>
                          <div className="text-sm font-medium text-accent-900">
                            {report.organizationType === 'EM' ? report.organizationName : (report.emLevel || "N/A")}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="w-8 h-8 rounded-full flex items-center justify-center mr-3 text-white font-bold bg-green-600">
                            D
                          </div>
                          <div className="text-sm font-medium text-accent-900">
                            {report.organizationType === 'DM' ? report.organizationName : (report.dmLevel || "N/A")}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="w-8 h-8 rounded-full flex items-center justify-center mr-3 text-white font-bold bg-yellow-600">
                            C
                          </div>
                          <div className="text-sm font-medium text-accent-900">
                            {report.organizationType === 'CM' ? report.organizationName : (report.cmLevel || "N/A")}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="w-8 h-8 rounded-full flex items-center justify-center mr-3 text-white font-bold bg-orange-600">
                            B
                          </div>
                          <div className="text-sm font-medium text-accent-900">
                            {report.organizationType === 'BM' ? report.organizationName : (report.bmLevel || "N/A")}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="w-8 h-8 rounded-full flex items-center justify-center mr-3 text-white font-bold bg-red-600">
                            A
                          </div>
                          <div className="text-sm font-medium text-accent-900">
                            {report.organizationType === 'AM' ? report.organizationName : (report.amLevel || "N/A")}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-accent-900">
                        <div>
                          <div className="font-medium">{report.agentName}</div>
                          <div className="text-xs text-accent-500">{report.agentId}</div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-accent-900">
                        ${safeToFixed(report.commissionRate)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-accent-900">
                        {report.totalSalesCount}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-accent-900">
                        ${safeToLocaleString(report.salesAmount)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-accent-900">
                        ${safeToFixed(report.overrideRate)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-accent-900">
                        {report.totalOverrideCount}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-accent-900">
                        ${safeToFixed(report.overrideAmount)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-accent-900">
                        {editingReport === report.id ? (
                          <input
                            type="text"
                            value={editData.bonuses || ""}
                            onChange={(e) => {
                              const value = e.target.value
                              // Allow only numbers, decimal point, and negative sign
                              if (value === '' || /^-?\d*\.?\d*$/.test(value)) {
                                setEditData({ ...editData, bonuses: value })
                              }
                            }}
                            className="input-field w-20"
                            placeholder="0.00"
                          />
                        ) : (
                          `$${safeToFixed(report.bonuses)}`
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-accent-900">
                        {editingReport === report.id ? (
                          <input
                            type="text"
                            value={editData.adjustments || ""}
                            onChange={(e) => {
                              const value = e.target.value
                              // Allow only numbers, decimal point, and negative sign
                              if (value === '' || /^-?\d*\.?\d*$/.test(value)) {
                                setEditData({ ...editData, adjustments: value })
                              }
                            }}
                            className="input-field w-20"
                            placeholder="0.00"
                          />
                        ) : (
                          <span className={report.adjustments >= 0 ? "text-green-600" : "text-red-600"}>
                            ${safeToFixed(report.adjustments)}
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-accent-900">
                        ${safeToLocaleString(report.totalCommission)}
                      </td>
                      {!isAgent && (
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-accent-900">
                          ${safeToLocaleString(report.totalDownlineRevenue)}
                        </td>
                      )}
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-accent-900">
                        {report.activeDownlineCount}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-accent-900">
                        {report.inactiveDownlineCount}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-accent-900">
                        {report.activeDownlineCount + report.inactiveDownlineCount}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {editingReport === report.id ? (
                          <select
                            className="input-field"
                            value={editData.status}
                            onChange={(e) => setEditData({ ...editData, status: e.target.value })}
                          >
                            <option value="PENDING">Pending</option>
                            <option value="APPROVED">Approved</option>
                          </select>
                        ) : (
                          <div className="flex items-center space-x-2">
                            {getStatusIcon(report.status)}
                            <span className={getStatusBadge(report.status)}>{report.status}</span>
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        {editingReport === report.id ? (
                          <div className="flex space-x-2">
                            <button
                              onClick={() => handleSave(report.id)}
                              className="text-green-600 hover:text-green-900 p-1 rounded"
                              title="Save changes"
                            >
                              <Save className="w-4 h-4" />
                            </button>
                            <button
                              onClick={handleCancel}
                              className="text-red-600 hover:text-red-900 p-1 rounded"
                              title="Cancel editing"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                        ) : (
                          <div className="flex space-x-2">
                            <button
                              onClick={() => handleEdit(report)}
                              className="text-primary-600 hover:text-primary-900 p-1 rounded"
                              title="Edit report"
                            >
                              <Edit className="w-4 h-4" />
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {paginatedReports.length === 0 && !loading && (
                <div className="text-center py-8">
                  <p className="text-accent-500">No pay reports found matching your criteria.</p>
                </div>
              )}
            </div>

            {/* Pagination Component - Same as Sales Report */}
            {totalPages > 1 && renderPagination()}
          </>
        )}
      </div>
    </div>
  )
}
